import { QueryObserver, QueryObserverOptions } from '@tanstack/query-core';

import {
  getScheduledReports,
  getDispatchedReports
} from '../../predict/src/core/api/reporting/report-scheduling';
import { getExportedReports } from '../../predict/src/core/api/reporting/report-document-export';
import { queryClient } from '../core/queryClient';

// ── Scheduled Reports ──

type GetScheduledReportsResult = Awaited<ReturnType<typeof getScheduledReports>>;
type ScheduledReportsQueryOptions = Omit<QueryObserverOptions<GetScheduledReportsResult>, 'queryKey' | 'queryFn'>;

export const reportSchedulesQueryKey = (organisationId: string, workspaceId: string, isConsolidation: boolean) => [
  'reportSchedules',
  organisationId,
  workspaceId,
  isConsolidation
];

export const reportSchedulesQuery = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean,
  options?: ScheduledReportsQueryOptions
): QueryObserver<GetScheduledReportsResult> =>
  new QueryObserver<GetScheduledReportsResult>(queryClient, {
    queryKey: reportSchedulesQueryKey(organisationId, workspaceId, isConsolidation),
    queryFn: () => getScheduledReports(workspaceId, organisationId, {}, isConsolidation),
    ...options
  });

// ── Awaiting Review Reports ──

type GetAwaitingReviewReportsResult = Awaited<ReturnType<typeof getDispatchedReports>>;
type AwaitingReviewReportsQueryOptions = Omit<QueryObserverOptions<GetAwaitingReviewReportsResult>, 'queryKey' | 'queryFn'>;

export const awaitingReviewReportsQueryKey = (organisationId: string, workspaceId: string, isConsolidation: boolean) => [
  'awaitingReviewReports',
  organisationId,
  workspaceId,
  isConsolidation
];

export const awaitingReviewReportsQuery = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean,
  options?: AwaitingReviewReportsQueryOptions
): QueryObserver<GetAwaitingReviewReportsResult> =>
  new QueryObserver<GetAwaitingReviewReportsResult>(queryClient, {
    queryKey: awaitingReviewReportsQueryKey(organisationId, workspaceId, isConsolidation),
    queryFn: () => getDispatchedReports(workspaceId, organisationId, { status: 'awaiting-permission' }, isConsolidation),
    ...options
  });

// ── Dispatched Reports ──

type GetDispatchedReportsResult = Awaited<ReturnType<typeof getDispatchedReports>>;
type DispatchedReportsQueryOptions = Omit<QueryObserverOptions<GetDispatchedReportsResult>, 'queryKey' | 'queryFn'>;

export const dispatchedReportsQueryKey = (organisationId: string, workspaceId: string, isConsolidation: boolean) => [
  'dispatchedReports',
  organisationId,
  workspaceId,
  isConsolidation
];

export const dispatchedReportsQuery = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean,
  options?: DispatchedReportsQueryOptions
): QueryObserver<GetDispatchedReportsResult> =>
  new QueryObserver<GetDispatchedReportsResult>(queryClient, {
    queryKey: dispatchedReportsQueryKey(organisationId, workspaceId, isConsolidation),
    queryFn: () => getDispatchedReports(workspaceId, organisationId, { status: 'dispatched' }, isConsolidation),
    ...options
  });

// ── Exported Reports ──

type GetExportedReportsResult = Awaited<ReturnType<typeof getExportedReports>>;
type ExportedReportsQueryOptions = Omit<QueryObserverOptions<GetExportedReportsResult>, 'queryKey' | 'queryFn'>;

export const exportedReportsQueryKey = (organisationId: string, workspaceId: string, isConsolidation: boolean) => [
  'exportedReports',
  organisationId,
  workspaceId,
  isConsolidation
];

export const exportedReportsQuery = (
  workspaceId: string,
  organisationId: string,
  isConsolidation: boolean,
  options?: ExportedReportsQueryOptions
): QueryObserver<GetExportedReportsResult> =>
  new QueryObserver<GetExportedReportsResult>(queryClient, {
    queryKey: exportedReportsQueryKey(organisationId, workspaceId, isConsolidation),
    queryFn: () => getExportedReports(workspaceId, organisationId, isConsolidation),
    ...options
  });
